import { Feather } from '@expo/vector-icons';
import { fontFamily } from '_/config/theme';
import themes from '_/constants/themes';
import { transparentize } from 'polished';
import React, { useRef } from 'react';
import {
  ActivityIndicator,
  FlatList,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { showMessage } from 'react-native-flash-message';

import WhatsappTag from '../WhatsappTag';
import { WhatsappGuest } from '_/screens/EditMeeting';
import { useTranslation } from 'react-i18next';

interface Props {
  whatsappArray: WhatsappGuest[];
  onAdd: (guest: { name: string; phone: string }) => void;
  onRemove: (phone: string) => void;
  onEndReached?: () => void;
  loadingMore?: boolean;
}

export default function WhatsappTagInput({
  whatsappArray,
  onAdd,
  onRemove,
  onEndReached,
  loadingMore,
}: Props) {
  const nameRef = useRef<TextInput>(null);
  const phoneRef = useRef<TextInput>(null);

  const [name, setName] = React.useState('');
  const [phone, setPhone] = React.useState('');
  const [nameFocused, setNameFocused] = React.useState(false);
  const [phoneFocused, setPhoneFocused] = React.useState(false);

  const { t: translate } = useTranslation();

  function handleAdd() {
    if (!name || !phone) {
      return showMessage({ message: translate('whatsappEmptyFields'), type: 'danger' });
    }
    if (!isValidPhone(phone)) {
      return showMessage({
        message: translate('whatsappInvalidPhone'),
        type: 'danger',
      });
    }
    onAdd({ name, phone });
    setName('');
    setPhone('');
    nameRef.current?.focus();
  }

  return (
    <View style={styles.wrapper}>
      <View style={styles.card}>
        <View style={[styles.inputRow, nameFocused && styles.inputRowFocused]}>
          <Feather
            name="user"
            size={16}
            color={nameFocused ? '#25D366' : themes.colors.darkQuaternary}
          />
          <TextInput
            ref={nameRef}
            placeholder={translate('whatsappNamePlaceholder')}
            placeholderTextColor={themes.colors.graySecondary}
            value={name}
            onChangeText={setName}
            style={styles.input}
            returnKeyType="next"
            onSubmitEditing={() => phoneRef.current?.focus()}
            onFocus={() => setNameFocused(true)}
            onBlur={() => setNameFocused(false)}
          />
        </View>

        <View style={styles.divider} />

        <View style={[styles.inputRow, phoneFocused && styles.inputRowFocused]}>
          <Feather
            name="phone"
            size={16}
            color={phoneFocused ? '#25D366' : themes.colors.darkQuaternary}
          />
          <TextInput
            ref={phoneRef}
            placeholder={translate('whatsappPhonePlaceholder')}
            placeholderTextColor={themes.colors.graySecondary}
            value={phone}
            keyboardType="phone-pad"
            onChangeText={setPhone}
            style={styles.input}
            onSubmitEditing={handleAdd}
            onFocus={() => setPhoneFocused(true)}
            onBlur={() => setPhoneFocused(false)}
          />
          <TouchableOpacity
            style={[styles.addButton, (!name || !phone) && styles.addButtonDisabled]}
            onPress={handleAdd}
            disabled={!name || !phone}
          >
            <Feather name="plus" size={16} color="#fff" />
          </TouchableOpacity>
        </View>
      </View>

      {whatsappArray.length > 0 && (
        <FlatList
          data={whatsappArray}
          keyExtractor={(item) => item.phone}
          scrollEnabled={false}
          nestedScrollEnabled
          renderItem={({ item }) => <WhatsappTag guest={item} removeTag={onRemove} />}
          onEndReached={onEndReached}
          onEndReachedThreshold={0.3}
          ListFooterComponent={
            loadingMore ? (
              <ActivityIndicator size="small" color="#25D366" style={{ marginVertical: 8 }} />
            ) : null
          }
          style={styles.tagList}
        />
      )}
    </View>
  );
}

function isValidPhone(phone: string) {
  const cleaned = phone.replace(/\D/g, '');
  return cleaned.length >= 10 && cleaned.length <= 13;
}

const styles = StyleSheet.create({
  wrapper: { marginTop: 8 },
  tagList: { marginTop: 12 },
  card: {
    borderWidth: 1.5,
    borderColor: themes.colors.lightSecondary,
    borderRadius: 10,
    overflow: 'hidden',
    backgroundColor: '#fff',
  },
  inputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 10,
    gap: 10,
    backgroundColor: '#fff',
  },
  inputRowFocused: {
    backgroundColor: transparentize(0.95, '#25D366'),
  },
  input: {
    flex: 1,
    fontSize: 14,
    fontFamily: fontFamily.regular,
    color: themes.colors.darkSecondary,
    paddingVertical: 2,
  },
  divider: {
    height: 1,
    backgroundColor: themes.colors.lightSecondary,
    marginHorizontal: 12,
  },
  addButton: {
    backgroundColor: '#25D366',
    borderRadius: 20,
    width: 30,
    height: 30,
    alignItems: 'center',
    justifyContent: 'center',
  },
  addButtonDisabled: {
    backgroundColor: themes.colors.graySecondary,
  },
});
